import { useGesture } from '@use-gesture/react';
import clsx from 'clsx';
import type { NumberValue, ScaleBand } from 'd3-scale';
import { useRef } from 'react';

import { axisContainerStyle } from './AxisArea.sty.js';
import { computeCollisionAwareMaxSpace } from './utils/compute-collision-aware-max-space.js';
import { isAxisPannable } from './utils/is-axis-pannable.js';
import { XTickWithTruncation } from './XTickWithTruncation.js';
import { XYChartPanningControls } from './XYChartPanningControls.js';
import { AxisBottom } from '../../../core/components/axes/AxisBottom.js';
import { DivAxisWrapper } from '../../../core/components/axes/components/DivAxisWrapper.js';
import {
  axisFontProps,
  EMOJI_REGEX,
} from '../../../core/components/axes/constants.js';
import { getXTickMaxSize } from '../../../core/components/axes/utils/calculate-axis-size.js';
import { getXAxisTickLabelProps } from '../../../core/components/axes/utils/get-tick-label-props.js';
import { isTickLabelSpaceAvailable } from '../../../core/components/axes/utils/is-tick-label-visible.js';
import { ChartGrid } from '../../../core/components/chart-grid/ChartGridRoot.js';
import { KEYBOARD_HANDLERS } from '../../../core/components/zoom/constants.js';
import { cursorStyle } from '../../../core/components/zoom/ZoomCursors.sty.js';
import { XY_CHART_BOTTOM_AXIS_LABEL } from '../../constants/defaults.js';
import { useAxesTicks } from '../../hooks/useAxesTicks.js';
import { useVisibleAxesConfig } from '../../hooks/useVisibleAxesConfig.js';
import { useVisibleAxesScales } from '../../hooks/useVisibleAxesScales.js';
import { useXYChartScales } from '../../hooks/useXYChartScales.js';
import { isCategorical } from '../../types/guards.js';
import { AREAS } from '../../utils/grid-templates.js';
import { useApplyPanDelta } from '../overlay/hooks/useApplyPanDelta.js';

export function XYChartBottomAxisArea() {
  const axisRef = useRef<HTMLDivElement>(null);

  const { bottomTicks } = useAxesTicks();
  const { bottom: bottomConfig } = useVisibleAxesConfig();
  const { bottom: bottomScale } = useVisibleAxesScales();
  const scales = useXYChartScales();

  const canPan = isAxisPannable(bottomConfig, scales);

  const tickFormat = (_: unknown, index: number) =>
    bottomTicks.formattedTicks[index];

  const isCategoricalAxis = bottomConfig?.type
    ? isCategorical(bottomConfig.type)
    : false;
  const isTickRotated = bottomConfig?.tickLabelLayout === 'vertical';
  const tickLabelProps = getXAxisTickLabelProps(
    'bottom',
    isCategoricalAxis,
    isTickRotated,
    bottomScale,
    bottomConfig?.tickLabelLayout,
  );

  const areCategoricalTicksNotFittable =
    bottomScale &&
    isCategoricalAxis &&
    !isTickLabelSpaceAvailable((bottomScale as ScaleBand<string>).step());

  const handlePan = useApplyPanDelta();

  useGesture(
    {
      onDrag: (e) =>
        KEYBOARD_HANDLERS.includes(e.type) ? null : handlePan(-e.delta[0]),
    },
    {
      target: axisRef,
      eventOptions: { passive: false },
      drag: { filterTaps: true, pointer: { buttons: [1, 4] } },
      enabled: canPan,
    },
  );

  const hideAxis =
    bottomConfig === undefined ||
    areCategoricalTicksNotFittable ||
    bottomScale === undefined;

  const tickNameHasEmoji = bottomTicks.ticks.some((name) =>
    EMOJI_REGEX.test(name),
  );

  return hideAxis ? null : (
    <ChartGrid.GridArea gridArea={AREAS.bottomAxis}>
      {({ availableWidth, availableHeight }) => {
        const maxSpaceForTruncation = computeCollisionAwareMaxSpace(
          getXTickMaxSize(
            availableHeight,
            availableWidth,
            isTickRotated,
            bottomScale,
            bottomConfig.label,
            bottomTicks.ticks.length,
          ),
          bottomTicks.formattedTicks,
          bottomTicks.ticks as unknown as NumberValue[],
          bottomScale,
          isCategoricalAxis,
          isTickRotated,
        );

        return (
          <div
            className={clsx(
              cursorStyle({ isPanEnabled: canPan }),
              axisContainerStyle,
            )}
            ref={axisRef}
          >
            {canPan && (
              <XYChartPanningControls
                visibleAxis={bottomConfig}
                onClickPan={(delta) => handlePan(-delta)}
              />
            )}
            <DivAxisWrapper
              divAxisStyle={axisFontProps}
              ticks={bottomTicks.formattedTicks}
              label={bottomConfig.label}
              isTickVertical
              isTickRotated={isTickRotated}
            >
              <AxisBottom
                role="group"
                aria-label={XY_CHART_BOTTOM_AXIS_LABEL}
                scale={bottomScale}
                containerSize={availableWidth}
                height={availableHeight}
                tickValues={bottomTicks.ticks}
                tickLabelProps={tickLabelProps}
                tickFormat={tickFormat}
                label={bottomConfig.label}
                tickComponent={(renderProps) => (
                  <XTickWithTruncation
                    renderProps={renderProps}
                    maxSpaceForTruncation={maxSpaceForTruncation}
                    centerTick={isCategoricalAxis}
                    isTickRotated={isTickRotated}
                    position="bottom"
                    tickNameHasEmoji={tickNameHasEmoji}
                  />
                )}
              />
            </DivAxisWrapper>
          </div>
        );
      }}
    </ChartGrid.GridArea>
  );
}

XYChartBottomAxisArea['displayName'] = 'XYChartValueBottomAxisArea';
